import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2, Check, Crown, Shield, Zap, Video, Star } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  { icon: Star,    label: "Neon-purple 'Pro' badge",          detail: "Everyone sees you're built different." },
  { icon: Video,   label: "500 MB uploads",                   detail: "5x the file size — push the frame at full fidelity." },
  { icon: Shield,  label: "Ad-free viewing",                  detail: "Zero pre-rolls, zero interruptions." },
  { icon: Zap,     label: "Priority processing queue",        detail: "Skip the line — your videos ship first." },
];

function StatusChip({ label, tone = "green" }) {
  const cls = tone === "pink"
    ? "border-[#FF007F]/40 bg-[#FF007F]/10 text-[#FF007F]"
    : "border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]";
  return (
    <span className={`inline-block px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
}

export default function Pro() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("cancelled")) {
      toast.error("Checkout cancelled");
      nav("/pro", { replace: true });
    }
  }, [location.search, nav]);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/subscription/plans");
        setPlans(p.data?.plans || []);
        if (user) {
          const m = await api.get("/subscription/me");
          setMe(m.data);
        }
      } catch (e) {
        console.warn("pro:load", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const startCheckout = async () => {
    if (!user) { nav("/login"); return; }
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/subscription/checkout", {
        lookup_key: "neosync_pro_monthly",
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const openPortal = async () => {
    if (openingPortal) return;
    setOpeningPortal(true);
    try {
      const r = await api.post("/subscription/portal", { return_url: `${window.location.origin}/pro` });
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal unavailable — enable Billing Portal in Stripe Dashboard.");
    } finally {
      setOpeningPortal(false);
    }
  };

  const plan = plans[0];
  const priceUsd = plan ? (plan.unit_amount / 100).toFixed(2) : "50.00";
  const isPro = !!me?.is_pro;
  const status = me?.pro_status;
  const cpe = me?.pro_current_period_end;
  const willCancel = !!me?.pro_cancel_at_period_end;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#B026FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="md:ml-60 min-h-screen pb-24 md:pb-12 relative overflow-hidden">
      {/* backdrop glow */}
      <div
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(176,38,255,0.35), transparent 60%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(255,0,127,0.35), transparent 60%)" }}
      />

      <div className="max-w-4xl mx-auto px-6 pt-10 space-y-10 relative">
        {/* hero */}
        <header>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF] mb-2">
            // subscription.tier
          </p>
          <h1
            className="font-display font-black text-5xl md:text-6xl mb-3 flex items-center gap-3 glitch"
            data-text="MatrixShopping PRO"
          >
            <Crown className="h-8 w-8 text-[#B026FF]" strokeWidth={2.2} />
            MatrixShopping PRO
          </h1>
          <p className="text-white/50 text-base font-mono max-w-2xl">
            Punch through the noise. Ad-free feed, bigger uploads, priority renders — for the price of one iced latte.
          </p>
        </header>

        {/* status card (only if signed in) */}
        {user && (
          <div
            data-testid="pro-status-card"
            className={`p-6 border ${isPro ? "border-[#B026FF]/60 bg-[#B026FF]/10" : "border-white/10 bg-[#0A0A0C]"}`}
          >
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                // your.tier
              </p>
              {isPro ? (
                <StatusChip label={status === "trialing" ? "trial" : "active"} />
              ) : (
                <StatusChip label="free" tone="pink" />
              )}
              {willCancel && <StatusChip label="cancels at period end" tone="pink" />}
            </div>
            <h2 className="mt-2 font-display font-black text-2xl text-white">
              {isPro ? "You're Pro." : "Currently on Free."}
            </h2>
            {isPro && cpe && (
              <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-1">
                {willCancel ? "access.ends" : "renews"} · {new Date(cpe * 1000).toLocaleDateString()}
              </p>
            )}
            {isPro && me?.has_subscription && (
              <button
                data-testid="pro-manage-btn"
                onClick={openPortal}
                disabled={openingPortal}
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 border border-[#B026FF]/50 hover:bg-[#B026FF]/20 text-white font-bold uppercase tracking-widest text-xs transition-all disabled:opacity-60"
              >
                {openingPortal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
                manage subscription
              </button>
            )}
          </div>
        )}

        {/* pricing card */}
        <div
          data-testid="pro-pricing-card"
          className="relative border border-[#B026FF]/40 bg-gradient-to-br from-[#B026FF]/10 via-black to-black p-8"
        >
          <div
            className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(176,38,255,0.35), transparent 70%)" }}
          />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B026FF]">
            // pro.pass
          </p>
          <div className="flex items-baseline gap-2 mt-2">
            <span
              className="font-display font-black text-7xl md:text-8xl text-white leading-none"
              style={{ textShadow: "0 0 30px rgba(176,38,255,0.5)" }}
            >
              ${priceUsd}
            </span>
            <span className="font-mono text-sm uppercase tracking-widest text-white/50">/ month</span>
          </div>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/40">
            billed monthly · cancel anytime · secured by stripe
          </p>

          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {PERKS.map(({ icon: Icon, label, detail }) => (
              <li key={label} className="flex items-start gap-3 p-3 border border-white/5 bg-black/40">
                <div className="h-8 w-8 shrink-0 flex items-center justify-center border border-[#B026FF]/40 bg-[#B026FF]/10">
                  <Icon className="h-4 w-4 text-[#B026FF]" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-[#39FF14]" strokeWidth={3} /> {label}
                  </p>
                  <p className="text-white/40 text-[11px] mt-0.5">{detail}</p>
                </div>
              </li>
            ))}
          </ul>

          {isPro ? (
            <div
              data-testid="pro-already-active"
              className="mt-6 w-full text-center py-4 border border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14] font-bold uppercase tracking-widest text-xs"
            >
              /// you're already flying pro
            </div>
          ) : (
            <button
              data-testid="pro-checkout-btn"
              onClick={startCheckout}
              disabled={submitting}
              className="mt-6 w-full bg-[#B026FF] text-white font-bold uppercase tracking-widest py-4 hover:shadow-[0_0_30px_rgba(176,38,255,0.7)] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> connecting to stripe…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  go pro — ${priceUsd}/mo
                </>
              )}
            </button>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 text-center mt-3">
            secured by stripe · cancel anytime
          </p>
        </div>

        <p className="text-white/30 text-xs text-center font-mono">
          Subscription auto-renews monthly. Manage or cancel anytime from the Stripe portal — no long-form emails required.
        </p>

        <div className="text-center pt-4">
          <button
            data-testid="pro-view-members-btn"
            onClick={() => nav("/pro/members")}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 hover:border-[#B026FF]/60 text-white/70 hover:text-white font-mono text-[10px] uppercase tracking-widest transition-all"
          >
            <Crown className="h-3.5 w-3.5 text-[#B026FF]" />
            see who else is pro →
          </button>
        </div>

        <p className="text-white/25 text-[10px] text-center font-mono uppercase tracking-widest pt-2">
          by subscribing you agree to our <a href="/terms" className="text-white/50 hover:text-white underline">terms</a> and <a href="/privacy" className="text-white/50 hover:text-white underline">privacy policy</a>
        </p>
      </div>
    </div>
  );
}
